import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import StorageIcon from '@mui/icons-material/Storage';

import redisAdminService, { RedisAdminStatus } from '../../services/redisAdminService';

const RedisSettingsPanel: React.FC = () => {
  const [status, setStatus] = useState<RedisAdminStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [flushLoading, setFlushLoading] = useState(false);
  const [flushDialogOpen, setFlushDialogOpen] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const data = await redisAdminService.getStatus();
      setStatus(data);
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'خطا در دریافت وضعیت Redis' });
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const statusChip = useMemo(() => {
    if (!status) return <Chip size="small" label="نامشخص" />;
    if (!status.enabled) return <Chip size="small" color="default" label="غیرفعال" />;
    if (status.connected) return <Chip size="small" color="success" label={status.mode === 'mock' ? 'متصل (Mock)' : 'متصل'} />;
    return <Chip size="small" color="warning" label="فعال ولی قطع" />;
  }, [status]);

  const onConfirmFlush = async () => {
    setFlushLoading(true);
    try {
      const res = await redisAdminService.flushDb();
      setToast({ type: 'success', message: res.message || 'Redis DB پاک شد' });
      setFlushDialogOpen(false);
      await fetchStatus();
    } catch (e: any) {
      setToast({ type: 'error', message: e?.message || 'خطا در FlushDB' });
    } finally {
      setFlushLoading(false);
    }
  };

  return (
    <Box dir="rtl" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {toast && (
        <Alert severity={toast.type} onClose={() => setToast(null)} sx={{ borderRadius: 2 }}>
          {toast.message}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                تنظیمات Redis
              </Typography>
              {statusChip}
            </Box>

            <Tooltip title="بروزرسانی وضعیت">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={loadingStatus ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={fetchStatus}
                  disabled={loadingStatus}
                >
                  بروزرسانی
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            وضعیت اتصال Redis و ابزارهای نگهداری/پاکسازی کش.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Ping
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status?.ping ?? '—')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                تعداد کلیدها (dbSize)
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status?.dbSize ?? '—')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Memory
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status?.info?.used_memory_human ?? '—')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Version
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus ? '...' : (status?.info?.redis_version ?? '—')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Connection
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {loadingStatus
                  ? '...'
                  : (status
                    ? `${status.connection.via.toUpperCase()} • ${status.connection.host}:${status.connection.port}`
                    : '—')}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setFlushDialogOpen(true)}
            disabled={flushLoading}
          >
            Flush DB
          </Button>
        </CardActions>
      </Card>

      <Dialog open={flushDialogOpen} onClose={() => setFlushDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>تایید پاکسازی Redis</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
            این عملیات کل دیتابیس Redis را پاک می‌کند و ممکن است باعث خروج کاربران/از بین رفتن session یا cache شود.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            اگر مطمئنی، تایید کن.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlushDialogOpen(false)} disabled={flushLoading}>
            انصراف
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmFlush}
            startIcon={flushLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
            disabled={flushLoading}
          >
            تایید و پاکسازی
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedisSettingsPanel;


